import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Account, Currency, Transaction, Vendor } from "@/lib/types";
import { currencyFormatter } from "@/lib/utils";
import { useGetAccounts } from "@/services/queries/accounts";
import { useGetCurrencies } from "@/services/queries/currency";
import { useGetVendors } from "@/services/queries/vendor";
import { format } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

function getVendorOrSourceName(
  tx: Transaction,
  accounts: Account[],
  vendors: Vendor[]
): string | undefined {
  if ("vendorId" in tx && tx.vendorId)
    return (
      vendors.find((v) => "vendorId" in tx && v.id === tx.vendorId)?.name ??
      undefined
    );
  if ("fromAccountId" in tx && tx.fromAccountId)
    return accounts.find((a) => a.id === tx.fromAccountId)?.name;
  if ("accountId" in tx && tx.accountId)
    return accounts.find((a) => a.id === tx.accountId)?.name;
  if ("source" in tx && tx.source?.trim())
    return accounts.find((a) => a.id === tx.accountId)?.name;
  if ("budgetName" in tx && tx.budgetName?.trim()) return tx.budgetName;
  if ("toAccountId" in tx && tx.toAccountId)
    return accounts.find((a) => a.id === tx.toAccountId)?.name;
  return undefined;
}

interface ReportsTableProps {
  transactions: Transaction[];
}

const ReportsTable = ({ transactions }: ReportsTableProps) => {
  const { t } = useTranslation();
  const { data: currenciesData } = useGetCurrencies();
  const { data: accountsData } = useGetAccounts();
  const { data: vendorsData } = useGetVendors();

  const currencies = useMemo(() => {
    return currenciesData?.value || [];
  }, [currenciesData]);

  const accounts = useMemo(() => {
    return accountsData?.value || [];
  }, [accountsData]);

  const vendors = useMemo(() => {
    return vendorsData?.value || [];
  }, [vendorsData]);

  if (transactions.length === 0) {
    return (
      <div className="mt-10 flex h-full items-center justify-center">
        <p className="text-white/60">{t("label.no_transactions_found")}</p>
      </div>
    );
  }

  return (
    <Table data-testid="reports-table">
      <TableBody>
        {transactions.map((transaction) => (
          <TransactionTableRow
            key={transaction.id}
            transaction={transaction}
            currencies={currencies}
            accounts={accounts}
            vendors={vendors}
          />
        ))}
      </TableBody>
    </Table>
  );
};

const TransactionTableRow = ({
  transaction,
  currencies,
  accounts,
  vendors,
}: {
  transaction: Transaction;
  currencies: Currency[];
  accounts: Account[];
  vendors: Vendor[];
}) => {
  const { t } = useTranslation();

  const currency = useMemo(() => {
    if ("currencyId" in transaction) {
      return currencies.find((c) => c.id === transaction.currencyId);
    }
  }, [currencies, transaction]);

  const vendorOrSource = getVendorOrSourceName(transaction, accounts, vendors);
  const description =
    "description" in transaction ? transaction.description : undefined;

  const hasVendorOrSource = vendorOrSource && vendorOrSource.trim().length > 0;
  const hasDescription = description?.trim();

  return (
    <TableRow data-testid={`reports-table-row-${transaction.id}`}>
      <TableCell className="flex items-center gap-3 py-3 pl-5 align-top">
        <span className="text-[13px] font-medium text-white/90">
          {format(transaction.transactionDate, "dd/MM/yyyy")}
        </span>

        <span className="h-fit rounded-full bg-white/10 px-2 text-[10px] font-medium text-white/80">
          {t(`label.${transaction.type.toLowerCase()}`, {
            defaultValue: transaction.type,
          })}
        </span>
      </TableCell>
      <TableCell className="px-4 py-3 align-top">
        <div className="flex flex-col gap-1">
          {hasVendorOrSource && (
            <span
              className="line-clamp-1 text-ellipsis text-[13px] font-medium text-white"
              title={vendorOrSource}
            >
              {vendorOrSource}
            </span>
          )}
          {hasDescription && (
            <span
              className="line-clamp-1 text-ellipsis text-[12px] text-white/55"
              title={description}
            >
              {description}
            </span>
          )}
          {!hasVendorOrSource && !hasDescription && (
            <span className="text-[12px] text-white/35">—</span>
          )}
        </div>
      </TableCell>
      <TableCell className="py-3 pr-5 text-right align-top">
        <div className="flex flex-col items-end gap-2">
          <span className="text-[14px] font-semibold tabular-nums text-[#7ECEE8]">
            {currencyFormatter(transaction.amount, currency?.code ?? "EUR")}
          </span>
        </div>
      </TableCell>
    </TableRow>
  );
};
export default ReportsTable;
